import { EntityAdapter, Update } from "@reduxjs/toolkit";
import { aeTitleRuleAdapter, backendValidationErrorAdapter, customizationBaseAdapter, dicomAttributeRuleAdapter, dicomRuleAdapter, doseOutputAdapter, doseRoiAdapter, DoseSliceState, doseTargetAdapter, modelAdapter, outputMetadataAdapter } from "../../dose/doseSlice";
import GenericSliceWrapper, { RemovedOutputEntities } from "./genericSliceWrapper";
import { DoseCustomizationEntities, DoseCustomizationOutput, DoseRoi, DoseTarget } from "../../dose/dose-types";

export class DoseSliceWrapper extends GenericSliceWrapper<DoseSliceState> {

    outputAdapter: EntityAdapter<DoseCustomizationOutput, string>;
    roiAdapter: EntityAdapter<DoseRoi, string>;
    targetAdapter: EntityAdapter<DoseTarget, string>;

    constructor() {
        super(
            modelAdapter,
            customizationBaseAdapter,
            outputMetadataAdapter,
            aeTitleRuleAdapter,
            dicomRuleAdapter,
            dicomAttributeRuleAdapter,
            backendValidationErrorAdapter,
        )
        this.outputAdapter = doseOutputAdapter;
        this.roiAdapter = doseRoiAdapter;
        this.targetAdapter = doseTargetAdapter;
    }

    protected findOutput(state: DoseSliceState, outputId: string): DoseCustomizationOutput | undefined {
        return state.doseOutputs.entities[outputId];
    }

    protected updateOutput(state: DoseSliceState, update: Update<DoseCustomizationOutput, string>): void {
        this.outputAdapter.updateOne(state.doseOutputs, update);
    }

    /** Collects which store entities need to be removed and updated when a customization output is removed. */
    public collectCustomizationOutputEntitiesFromStoreForRemoval(state: DoseSliceState, output: DoseCustomizationOutput, customizationBaseId: string): RemovedOutputEntities {

        const roiIdsToBeRemoved: string[] = [];
        const targetIdsToBeRemoved: string[] = [];
        const metadataIdsToBeRemoved: string[] = [];

        metadataIdsToBeRemoved.push(...output.metadata);
        roiIdsToBeRemoved.push(...output.rois);
        targetIdsToBeRemoved.push(...output.targets);

        return {
            roiIdsToBeRemoved,
            targetIdsToBeRemoved,
            metadataIdsToBeRemoved,
            allRemovedIds: [...roiIdsToBeRemoved, ...targetIdsToBeRemoved, ...metadataIdsToBeRemoved],
        };
    };



    protected removeOutputRelatedItems(state: DoseSliceState, outputIds: string[], items: RemovedOutputEntities) {
        const { roiIdsToBeRemoved, metadataIdsToBeRemoved, targetIdsToBeRemoved } = items;

        if (roiIdsToBeRemoved) { this.roiAdapter.removeMany(state.doseRois, roiIdsToBeRemoved); }
        if (targetIdsToBeRemoved) { this.targetAdapter.removeMany(state.doseTargets, targetIdsToBeRemoved); }
        if (metadataIdsToBeRemoved) { this.outputMetadataAdapter.removeMany(state.outputMetadata, metadataIdsToBeRemoved); }
        this.outputAdapter.removeMany(state.doseOutputs, outputIds);
    }

    protected setOutputRelatedItemsAsModified(state: DoseSliceState, entitiesToUpdate: Partial<DoseCustomizationEntities>) {
        const roiIds = entitiesToUpdate.doseRois?.map(id => id.id);
        const targetIds = entitiesToUpdate.doseTargets?.map(id => id.id);
        const customizationOutputsIds = entitiesToUpdate.doseOutputs?.map(id => id.id);

        if (roiIds && roiIds.length > 0) {
            this.roiAdapter.updateMany(
                state.doseRois,
                roiIds.map(roiId => ({ id: roiId, changes: { isModified: true } }))
            );
        }

        if (targetIds && targetIds.length > 0) {
            this.targetAdapter.updateMany(
                state.doseTargets,
                targetIds.map(targetId => ({ id: targetId, changes: { isModified: true } }))
            );
        }

        if (customizationOutputsIds && customizationOutputsIds.length > 0) {
            this.outputAdapter.updateMany(
                state.doseOutputs,
                customizationOutputsIds.map(customizationOutputId => ({ id: customizationOutputId, changes: { isModified: true } }))
            );
        }
    }

}
